import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { z } from "zod";
import { AppShell } from "@/components/layouts/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ApiError } from "@/lib/api";
import { signUp } from "@/lib/authClient";
import { setPendingCredentials } from "@/lib/pendingCredentials";
import { usePersona } from "@/lib/persona";

const searchSchema = z.object({
  email: z.preprocess((value) => value === "" ? undefined : value, z.string().email().optional()),
  returnTo: z.string().optional(),
});
export const Route = createFileRoute("/signup")({
  validateSearch: searchSchema,
  component: SignupPage,
});

function SignupPage() {
  const persona = usePersona();
  const navigate = useNavigate();
  const search = Route.useSearch();

  const [email, setEmail] = useState(search.email ?? "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const emailSearch = email.trim() || undefined;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);

    if (password !== confirmPassword) {
      setErrorMsg("Passwords don't match. Please re-enter your password below.");
      return;
    }

    setSubmitting(true);
    try {
      const emailLc = email.trim().toLowerCase();
      // Don't send `locale`: the Custom Message Lambda defaults to bilingual
      // EN-first when no locale hint is present. Sending "es" from
      // navigator.language would flip the email to Spanish-only, which
      // isn't what we want for a mostly-US-audience festival (2026-07-18).
      // When we ever want per-user locale, wire this back but also update
      // the Lambda to keep bilingual as the default.
      await signUp({ email: emailLc, password, name: name.trim() || undefined });
      // Stash for the auto-signin bridge in /confirm (module-scope, per-tab)
      setPendingCredentials(emailLc, password);
      void navigate({
        to: "/confirm" as never,
        search: { email: emailLc, returnTo: search.returnTo } as never,
        replace: true,
      });
    } catch (e) {
      if (e instanceof ApiError) {
        setErrorMsg(e.problem.detail ?? "Sign-up failed. Please try again.");
      } else {
        setErrorMsg("Sign-up failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AppShell>
      <section className="container py-16 md:py-20">
        <Card className="mx-auto max-w-md">
          <CardHeader>
            <CardTitle className="font-serif text-3xl text-palm">
              {search.returnTo?.startsWith("/app/venue") ? "Create your venue account" : "Create your account"}
            </CardTitle>
            <CardDescription>
              Join <span className="font-medium text-foreground">{persona.brand.name}</span>. Free forever, one code away.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Your name</Label>
                <Input
                  id="name"
                  type="text"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Doe"
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  disabled={submitting}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 8 characters"
                  minLength={8}
                  disabled={submitting}
                />
                <p className="text-xs text-muted-foreground">
                  Min 8 chars, with upper, lower, and a number.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm password</Label>
                <Input
                  id="confirmPassword"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat your password"
                  minLength={8}
                  disabled={submitting}
                  aria-invalid={confirmPassword.length > 0 && confirmPassword !== password ? true : undefined}
                />
                {confirmPassword.length > 0 && confirmPassword !== password && (
                  <p className="text-xs text-destructive">Passwords don't match yet.</p>
                )}
              </div>

              {errorMsg && (
                <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {errorMsg}
                </div>
              )}

              <Button type="submit" size="lg" className="w-full" variant="accent" disabled={submitting || password.length < 8 || password !== confirmPassword}>
                {submitting ? "Creating…" : "Create account"}
              </Button>

              <p className="text-center text-sm text-muted-foreground">
                Already have an account?{" "}
                <Link to={"/login" as never} search={{ email: emailSearch, returnTo: search.returnTo } as never}
                  className="text-palm underline underline-offset-2 hover:text-accent">
                  Sign in
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </section>
    </AppShell>
  );
}
